import { ReactElement, ReactNode, useState } from 'react';
import { Spinner } from '@weareopenr/component-library';
import dayjs from 'dayjs';
import { Control, FieldValues, useForm, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { useStepNavigation } from 'src/hooks/useStepNavigation/useStepNavigation';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { formatScheduledTime, publishTimes } from 'src/components/PublishDetailsView/shared';
import PublishView from '../PublishView';
import PublishFlowFooter from '../PublishFlowFooter/PublishFlowFooter';
import { NoChannelsView } from '../NoChannelsView/NoChannelsView';
import { errorNotification, successNotification } from '../PublishView/Notifications';
import { getVenuesWithChannels } from '../utilities/getVenuesWithChannels/getVenuesWithChannels';
import { getAvailableChannelIds } from '../utilities/getAvailableChannelIds/getAvailableChannelIds';
import { IPublishDestination, PublishSteps } from '../types';
import styles from '../PublishModal.module.scss';

const steps = [PublishSteps.Setup, PublishSteps.Venues, PublishSteps.Summary];

export interface IPublishFlowForm {
  control: Control<FieldValues>;
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}

export interface IPublishContext {
  /** ISO-ish local date time when scheduled, '' when publishing now. */
  publicationDateTime: string;
  isScheduled: boolean;
  /** Shows the app-wide success snackbar; call it once the publish request has been accepted. */
  notifySuccess: (channelNames: string[]) => void;
}

interface IPublishFlowProps {
  /** Shown in notifications and used as the default schedule name. */
  entityName: string;
  publicationData: IPublishDestination[];
  defaultVenueIds: string[];
  /** Extra form defaults a flow needs on top of the shared fields, e.g. menu mealtimes. */
  defaultValues?: FieldValues;
  requirePriceScheme: boolean;
  showSchedule: boolean;
  /** Eyebrow on the venues step. */
  eyebrow?: string;
  closeModal: () => void;
  renderSetup: (form: IPublishFlowForm) => ReactNode;
  renderSummary: (form: IPublishFlowForm) => ReactNode;
  /** Sends the publish request and moves on. Throwing shows the error inside the modal. */
  onPublish: (values: FieldValues, context: IPublishContext) => Promise<void>;
  errorTags: Record<string, string>;
}

/**
 * The Setup → Venues → Summary publish flow shared by menu and Menu Set publish: form state, step
 * navigation, the Continue rules, the footer and submit error handling. Each modal supplies its setup
 * and summary views and the request it sends.
 */
export const PublishFlow = ({
  entityName,
  publicationData,
  defaultVenueIds,
  defaultValues,
  requirePriceScheme,
  showSchedule,
  eyebrow,
  closeModal,
  renderSetup,
  renderSummary,
  onPublish,
  errorTags,
}: IPublishFlowProps): ReactElement => {
  const { captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { step, incrementStep, decrementStep } = useStepNavigation(steps, PublishSteps.Setup);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, watch, register, setValue, handleSubmit } = useForm<FieldValues>({
    defaultValues: {
      'price-scheme': undefined,
      publicationType: publishTimes.NOW,
      publicationDate: '',
      scheduleName: entityName,
      selectedChannels: [],
      venues: defaultVenueIds,
      confirmationCheckbox: false,
      ...defaultValues,
    },
  });
  const form: IPublishFlowForm = { control, register, watch };

  const getButtonDisabledState = (values: FieldValues, step: PublishSteps) => {
    switch (step) {
      case PublishSteps.Setup: {
        const hasPriceScheme = !requirePriceScheme || values['price-scheme'] !== undefined;
        const hasChannel = getAvailableChannelIds(publicationData, values['selectedChannels']).length > 0;
        const scheduleReady =
          values['publicationType'] !== publishTimes.SCHEDULE ||
          (Boolean(values['confirmationCheckbox']) &&
            dayjs(values['publicationDate']).isValid() &&
            dayjs(values['publicationDate']).isAfter(dayjs()));
        return !hasPriceScheme || !hasChannel || (showSchedule && !scheduleReady);
      }
      case PublishSteps.Venues:
        // Count only venues that still belong to a selected channel — a venue can be left checked
        // in the flat map after its channel is de-selected on the setup step.
        return getVenuesWithChannels(publicationData, values['selectedChannels'], values['venues']).length === 0;
      case PublishSteps.Summary:
        return (
          isSubmitting ||
          getVenuesWithChannels(publicationData, values['selectedChannels'], values['venues']).length === 0
        );
      default:
        return false;
    }
  };

  const isSummaryStep = step === PublishSteps.Summary;
  const isScheduled = watch('publicationType') === publishTimes.SCHEDULE;
  // Summary and Setup close the modal; only the Venues step steps back.
  const leftButtonIsBack = step === PublishSteps.Venues;
  const leftButtonAction = leftButtonIsBack ? decrementStep : closeModal;

  const values = watch();
  const continueDisabled = getButtonDisabledState(values, step);

  const handlePublish = async (values: FieldValues) => {
    clearError();
    setIsSubmitting(true);

    const publicationDateTime = formatScheduledTime(values['publicationType'], dayjs(values['publicationDate']));

    try {
      await onPublish(values, {
        publicationDateTime,
        isScheduled: values['publicationType'] === publishTimes.SCHEDULE,
        notifySuccess: (channelNames) =>
          showNotification(successNotification(entityName, channelNames, publicationDateTime)),
      });
    } catch (error) {
      setIsSubmitting(false);
      captureException(error, { tags: errorTags });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      showNotification(errorNotification(entityName, publicationDateTime, errorMsg));
    }
  };

  const summaryLabel = isScheduled ? 'Schedule publish' : 'Publish now';
  const primaryLabel = isSummaryStep ? summaryLabel : 'Continue';

  return (
    <div className={styles['publish-flow']}>
      <main>
        <form onSubmit={handleSubmit(handlePublish)}>
          {step === PublishSteps.Setup && renderSetup(form)}
          {step === PublishSteps.Venues && (
            <PublishView
              selectedVenueIds={values['venues'] ?? []}
              onVenuesChange={(venueIds) => setValue('venues', venueIds, { shouldDirty: true })}
              publicationData={publicationData}
              selectedChannelIds={values['selectedChannels'] ?? []}
              eyebrow={eyebrow}
            />
          )}
          {step === PublishSteps.Summary && renderSummary(form)}
          <PublishFlowFooter
            leftButtonIsBack={leftButtonIsBack}
            onLeftButtonClick={leftButtonAction}
            isSummaryStep={isSummaryStep}
            onIncrementStep={incrementStep}
            continueDisabled={continueDisabled}
            isSubmitting={isSubmitting}
            primaryLabel={primaryLabel}
            errorMessage={errorMessage}
          />
        </form>
      </main>
    </div>
  );
};

/** The loading and "no channels" states every publish modal shows before its flow. */
export const PublishFlowGate = ({
  isLoading,
  isUnavailable,
  children,
}: {
  isLoading: boolean;
  isUnavailable: boolean;
  children: ReactNode;
}): ReactElement => {
  if (isLoading) {
    return (
      <div className={styles['publish-flow']}>
        <Spinner size="large" data-testid="publish-loading" />
      </div>
    );
  }

  if (isUnavailable) {
    return (
      <div className={styles['publish-flow']}>
        <NoChannelsView />
      </div>
    );
  }

  return <>{children}</>;
};
